// Client-side game state. Mostly a mirror of server snapshots, plus the
// local racer's own simulated state (which the *client* owns under our
// client-authoritative model).

export class GameState {
  constructor() {
    this.clientId = null;
    this.displayName = null;

    // Lobby vs. racing vs. finished.
    this.phase = 'lobby';
    // True when this client is watching someone else's race (racerId == "").
    // Toggles input handling, pose-send, and the SPECTATING overlay.
    this.isSpectator = false;

    // Race metadata.
    this.raceId = null;
    this.racerId = null;
    this.racers = [];        // [{racerId, displayName, startLane}]
    this.track = null;
    this.countdownEnds = 0;
    // Cup progress (1-based index, total). Surfaced as "RACE 1/3" etc.
    this.cupRaceIndex = 0;
    this.cupTotalRaces = 0;
    // Standings from the just-finished race when this is race 2+ of a cup.
    // Cleared when no previous race exists.
    this.previousStandings = null;

    // Local racer state (client-simulated).
    this.local = {
      position: { x: 0, y: 0, z: 0 },
      velocity: { x: 0, y: 0, z: 0 },
      yaw: 0,
      pitch: 0,
      roll: 0,
      vy: 0,
      grounded: true,
      tucking: false,
      lastCheckpoint: 0,
      trickState: '',
      // Visual-only front-flip angle (radians) applied around the racer's
      // local X axis. Set when a charged jump fires or when a ramp launches
      // them; integrated toward 2π by the GameLoop, then reset on landing.
      flipAngle: 0,
      flipRemainingSec: 0,
      // Visual carve lean (radians) around the racer's forward axis. Eases
      // toward -steer * LEAN_PER_STEER each frame so turning the joystick
      // visibly tilts the rider into the turn.
      leanAngle: 0,
      // Stun: when > 0, the racer is frozen (a tree hit, typically). Counts
      // down to 0 in the physics step.
      stunSec: 0,
      // Shield: epoch-ms timestamp at which the active shield expires. The
      // server is authoritative — we set it locally on use for instant
      // visual feedback, then trust the server's broadcast thereafter.
      shieldUntilMs: 0,
      // Tree just hit; ignore re-collision with this obstacle until the
      // post-stun grace timer expires so the player can move away cleanly.
      ignoreObstacleUntil: 0,
      // True after the local racer crosses the finish line. They decelerate
      // to a halt and input is ignored from that point.
      finished: false,
      // When a rocket hits the local racer, applyHit sets this to the
      // impact position; GameLoop reads it next frame to spawn an
      // explosion at the right spot then clears it back to null.
      pendingExplosionAt: null,
    };

    // Remote racer poses, keyed by racerId. Each value is the most recent
    // pose from the server; the renderer will interpolate towards these.
    this.remote = new Map();

    // Authoritative state from server.
    this.itemBoxes = [];
    this.activeItems = [];
    this.standings = [];     // racerIds in race order
    this.heldItem = '';
    this.lastServerTime = 0;

    // Final results when the race ends.
    this.results = null;
    // Cup standings (cumulative places across the cup's races). Populated
    // by race_over at the end of the cup; used for the podium ceremony and
    // the results-screen list.
    this.cupStandings = null;
  }

  startRace(payload) {
    this.phase = 'countdown';
    this.raceId = payload.raceId;
    this.racerId = payload.racerId;
    this.isSpectator = !payload.racerId; // empty racerId = spectator mode
    this.racers = payload.racers || [];
    this.track = payload.track || null;
    this.countdownEnds = payload.countdownEnds || 0;
    this.cupRaceIndex = payload.cupRaceIndex || 0;
    this.cupTotalRaces = payload.cupTotalRaces || 0;
    this.previousStandings = (payload.previousStandings || []).length > 0
      ? payload.previousStandings
      : null;
    this.remote.clear();
    this.standings = [];
    this.heldItem = '';
    this.results = null;

    // Place the local racer at their start lane.
    const me = this.racers.find((r) => r.racerId === this.racerId);
    const start = this.track?.startPositions?.[me?.startLane ?? 0];
    if (start) {
      this.local.position = { ...start };
    }
    this.local.velocity = { x: 0, y: 0, z: 0 };
    this.local.lastCheckpoint = 0;
    this.local.stunSec = 0;
    this.local.shieldUntilMs = 0;
    this.local.ignoreObstacleUntil = 0;
    this.local.finished = false;
    this.local.flipAngle = 0;
    this.local.flipRemainingSec = 0;
    this.local.leanAngle = 0;
    this.local.pendingExplosionAt = null;
  }

  applyServerUpdate(payload) {
    this.lastServerTime = payload.serverTime;
    this.itemBoxes = payload.itemBoxes || [];
    this.activeItems = payload.activeItems || [];
    this.standings = payload.standings || [];

    for (const pose of payload.remoteRacers || []) {
      this.remote.set(pose.racerId, pose);
    }

    if (payload.raceStatus === 'racing' && this.phase === 'countdown') {
      this.phase = 'racing';
    }
  }

  applyHit(payload) {
    // Server told us we got hit by an item. Plant the racer face-first
    // into the snow (upside-down with the body buried below the surface
    // so only the board sticks out) and queue an explosion at their
    // current position. The GameLoop reads pendingExplosionAt next frame
    // and spawns the VFX; when stunSec drains to 0 it triggers the
    // jump-and-flip-back recovery animation.
    if (payload?.stunMs) {
      this.local.stunSec = Math.max(this.local.stunSec, payload.stunMs / 1000);
      this.local.flipAngle = Math.PI;
      this.local.flipRemainingSec = 0;
      this.local.vy = 0;
      this.local.velocity.x = 0;
      this.local.velocity.z = 0;
      this.local.grounded = true;
      this.local.pendingExplosionAt = { ...this.local.position };
    }
    if (payload?.dropItem) {
      this.heldItem = '';
    }
  }

  finishRace(payload) {
    this.phase = 'finished';
    this.results = payload.standings || [];
    this.cupStandings = payload.cupStandings || null;
  }

  // Reset to lobby state after a race ends. Clears race-specific data so
  // the next race starts clean and the lobby screen takes over.
  returnToLobby() {
    this.phase = 'lobby';
    this.raceId = null;
    this.racerId = null;
    this.racers = [];
    this.track = null;
    this.countdownEnds = 0;
    this.remote.clear();
    this.standings = [];
    this.heldItem = '';
    this.itemBoxes = [];
    this.activeItems = [];
    this.results = null;
    this.cupStandings = null;
    this.cupRaceIndex = 0;
    this.cupTotalRaces = 0;
    this.previousStandings = null;
    this.isSpectator = false;
    this.local.position = { x: 0, y: 0, z: 0 };
    this.local.velocity = { x: 0, y: 0, z: 0 };
    this.local.lastCheckpoint = 0;
    this.local.stunSec = 0;
    this.local.shieldUntilMs = 0;
    this.local.ignoreObstacleUntil = 0;
    this.local.finished = false;
    this.local.flipAngle = 0;
    this.local.flipRemainingSec = 0;
    this.local.leanAngle = 0;
    this.local.yaw = 0;
    this.local.pendingExplosionAt = null;
  }

  myPlace() {
    const idx = this.standings.indexOf(this.racerId);
    return idx >= 0 ? idx + 1 : 0;
  }

  racerCount() {
    return this.racers.length;
  }
}
